<template>
  <n-drawer
    v-model="visible"
    :class="['mate-micro-drawer', `mate-micro-drawer--${drawerModeRef}`]"
    :close-on-press-escape="false"
    :close-on-click-modal="false"
    :primary-button="!drawerPropsRef.disablePrimaryButton"
    :secondary-button="!drawerPropsRef.disableSecondaryButton"
    :primary-text="drawerPropsRef.primaryText || '确定'"
    :secondary-text="drawerPropsRef.primaryText || '取消'"
    :mask-close="!drawerPropsRef.disableMaskClose"
    :size="drawerSize"
    @close="onClose"
  >
    <template #title>
      <n-row>
        <n-col :span="5"><div class="custom-drawer-title_left">
          <div class="cursor-pointer icon-btn mr-1" :class="{
          'icon_disabled': isPreRowDisabled,
        }">
            <NIcon @click="onPreRow" name="arrow_up" class="cursor-pointer" /></div>
          <div class="cursor-pointer icon-btn mr-1" :class="{
          'icon_disabled': isNextRowDisabled,
        }">
            <NIcon @click="onNextRow" class="cursor-pointer" name="arrow_down" /></div>
          <div class="cursor-pointer icon-btn mr-1"><NIcon name="refresh" @click="onDrawerRefresh" /></div>
        </div></n-col>
        <n-col :span="14">
          <!-- TODO lego@1.7.4 drawer中v-tooltip 不生效 -->
          <div class="mx-3">
            <n-tooltip placement="top" :disabled="isTitleOverflowDisabled">
              <div ref="titleRef" class="custom-drawer-title_content mt-1 px-3">{{drawerOpenOptions.drawerProps?.title}}</div>
              <template #content>{{drawerOpenOptions.drawerProps?.title}}</template>
            </n-tooltip>
          </div>
        </n-col>
        <n-col :span="5"><div class="custom-drawer-title_right">
          <div v-if="isMinFullScreenVisible" class="cursor-pointer icon-btn mr-1"><NIcon :name="drawerIsUnfold ? 'minimize' : 'full_screen'" @click="onTrigger" /></div>
          <div v-if="isOpenNewTabVisible" class="cursor-pointer icon-btn mr-1"><NIcon name="jump" @click="onOpenTab" /></div>
          <div class="cursor-pointer icon-btn mr-1"><NIcon name="close" @click="onDrawerClose" /></div>
        </div></n-col>
      </n-row>
    </template>
    <div ref="wrapRef" style="height: 100%; width: 100%;">
      <iframe
        v-if="drawerModeRef === 'iframe' && iframeSrc && visible"
        :key="domRefreshKey"
        ref="iframeRef"
        name="appIframe"
        :src="iframeSrc"
        width="100%"
        height="100%"
        frameborder="no"
        border="0"
        marginwidth="0"
        marginheight="0"
        scrolling="auto"
        allowtransparency="true"
      ></iframe>
    </div>
  </n-drawer>
</template>
<script setup lang="ts" name="MicroDrawer">
import { computed, defineExpose, getCurrentInstance, nextTick, onMounted, ref } from 'vue'
import { $message, NCol, NDrawer, NRow, NTooltip } from '@nio-fe/lego'
import { NIcon } from '@nio-fe/icon'
import useMatestoreStore from '@/store/matestoreStore'
import { getRouterName } from '@/utils/router'
import useGlobalStore from '@/store/globalStore'

type DrawerMode = 'iframe';
type OpenDrawerOptions = {
  appCode?: string;
  pageCode?: string;
  appName?: string;
  path?: string;
  params?: Record<string, any>;
  drawerProps?: DrawerPropsOption;
  version?: number;
  openAsIframe?: boolean;
};

interface DrawerPropsOption {
  disablePrimaryButton?: boolean;
  disableSecondaryButton?: boolean;
  disableMaskClose?: boolean;
  primaryText?: string;
  secondaryText?: string;
  unfold?: boolean;
  showCloseBtn?: boolean;
  title?: string;
  width?: number | string;
  onDrawerReady: (params: any) => void;
  onNextRow: (options: OpenDrawerOptions) => Promise<void>;
  onPreRow: (options: OpenDrawerOptions) => Promise<void>;
}

const drawerOpenOptions = ref<Partial<OpenDrawerOptions>>({});
const visible = ref<boolean>(false);
const drawerModeRef = ref<DrawerMode>('micro-app');
const drawerIsUnfold = ref<boolean>(false);
const iframeSrc = ref<string | undefined>('');
const wrapRef = ref<HTMLElement | null>(null);
const iframeRef = ref();
const titleRef = ref();
const isTitleOverflowDisabled = ref(false);
const domRefreshKey = ref(Date.now());
const isPreRowDisabled = ref(true);
const isNextRowDisabled = ref(true);

const drawerInstance = getCurrentInstance();
const globalStore = useGlobalStore();

const matestoreStore = useMatestoreStore();

const drawerSize = computed(() => {
  if (drawerOpenOptions.value.drawerProps?.width) return drawerOpenOptions.value.drawerProps?.width;
  if (drawerIsUnfold.value) {
    return 'calc(100% - 76px)';
  } else {
    return 414;
  }
});

// width 由外部传入视为固定宽度，不允许最大化/最小化drawer
const isMinFullScreenVisible = computed(() => !drawerPropsRef.value?.width);
const isOpenNewTabVisible = computed(() => drawerModeRef.value === 'iframe' && iframeSrc.value);

const drawerPropsRef = computed(() => {
  return drawerOpenOptions.value.drawerProps || {} as DrawerPropsOption;
});

const userDetailStupidPatch = (options: OpenDrawerOptions) => {
  const isUserDetail = options.path === '/user/detail';
  const hasUserBookAuthority = globalStore.hasAuthority('user_detail_normal');
  const hasOpenLeadsDetailAuthority =
    globalStore.hasAuthority('user_detail_leads');
  if (!isUserDetail) {
    return options;
  }
  if (hasOpenLeadsDetailAuthority && !hasUserBookAuthority) {
    return {
      path: `/leads#/detail/${options.params?.userCode}${
        options?.params?.brand ? `?brand=${options.params?.brand}` : ''
      }`,
      openAsIframe: true,
      drawerProps: options.drawerProps,
    };
  } else if (!hasUserBookAuthority && !hasOpenLeadsDetailAuthority) {
    $message.error('您没有权限查看该用户的详细信息');
    throw new Error('您没有权限查看该用户的详细信息');
  }
  return options;
};

const isTextOverflow = (element) => {
  return element.scrollWidth > element.clientWidth;
}

const updateTitleOverflowStatus = () => {
  isTitleOverflowDisabled.value = !!(titleRef.value && !isTextOverflow(titleRef.value));
}

const onDrawerReady = () => {
  const onDrawerReady = drawerOpenOptions.value.drawerProps?.onDrawerReady
  if (!onDrawerReady) return;
  onDrawerReady({
    api: drawerInstance?.exposed,
  })
}

const getAppName = () => {
  let { appCode, appName, pageCode } = drawerOpenOptions.value;
  if (appCode && !appName) {
    appName = getRouterName({ appCode, pageCode });
  }
  if (!appCode && !appName) {
    return '';
  }
  return appName;
};

const appShouldUseIframeMode = computed(() => {
  const { openAsIframe } = drawerOpenOptions.value;
  if (openAsIframe) {
    return openAsIframe;
  }
  const appName = getAppName();
  const microData = matestoreStore.microMap[appName as string];
  if (!microData) {
    return false;
  }
  return microData?.isIframe;
});

const initIframeSrc = () => {
  let { path, params } = drawerOpenOptions.value;

  if (path) {
    path = path.replace(window.location.origin, '');
    if (/https:\/\//.test(path)) {
      iframeSrc.value = path;
    } else {
      if (!path.includes('/iframe-container')) {
        path = `/iframe-container${path}`;
      }
      iframeSrc.value = `${window.location.origin}${path}`;
    }
  } else {
    const appName = getAppName();
    const microData = matestoreStore.microMap[appName as string];
    if (!microData) {
      return;
    }
    if (!microData?.isIframe) {
      return;
    }
    let entry = microData.entry;
    const p = params || {};
    if (p && entry) {
      Object.keys(p).forEach((key) => {
        entry = entry?.replace(`{${key}}`, p[key]);
      });
    }
    iframeSrc.value = entry;
  }
};

function renderContent() {
  const { drawerProps } = drawerOpenOptions.value;
  if (appShouldUseIframeMode.value) {
    drawerModeRef.value = 'iframe';
  }
  drawerIsUnfold.value = !!drawerProps?.unfold;

  visible.value = true;
  nextTick(() => {
    initIframeSrc();
    setTimeout(() => {
      updateTitleOverflowStatus();
    }, 500);
  });
}

function openDrawer(options: OpenDrawerOptions) {
  options = userDetailStupidPatch(options);
  let { drawerProps } = options;
  drawerOpenOptions.value = options;

  iframeSrc.value = '';

  renderContent();

  setTimeout(() => {
    // 通知drawer ready
    onDrawerReady();
  }, 500);
}

function onClose(isUnmount?: boolean) {
  visible.value = false;
  iframeSrc.value = '';
}
function onTrigger() {
  drawerIsUnfold.value = !drawerIsUnfold.value;
}
// 展开
function onUnfold() {
  drawerIsUnfold.value = true;
}
// 折叠
function onFold() {
  drawerIsUnfold.value = false;
}

function onPreRow() {
  if (isPreRowDisabled.value || typeof drawerPropsRef.value?.onPreRow !== 'function') return;

  // 剔除drawerOpenOptions.value中的Function
  let options = JSON.parse(JSON.stringify(drawerOpenOptions.value));
  // 将iframe重新渲染
  domRefreshKey.value = Date.now();
  options = drawerPropsRef.value.onPreRow(options) as OpenDrawerOptions;
  if (!options) return;
  options = userDetailStupidPatch(options);
  drawerOpenOptions.value = {
    ...options,
    drawerProps: {
      ...drawerOpenOptions.value.drawerProps, // 恢复function引用
      ...options.drawerProps,
    },
  };

  renderContent();
}

function onNextRow() {
  if (isNextRowDisabled.value || typeof drawerPropsRef.value?.onNextRow !== 'function') return;

  // 剔除drawerOpenOptions.value中的Function
  let options = JSON.parse(JSON.stringify(drawerOpenOptions.value));
  // 将iframe重新渲染
  domRefreshKey.value = Date.now();
  options = drawerPropsRef.value.onNextRow(options) as OpenDrawerOptions;
  if (!options) return;
  options = userDetailStupidPatch(options);
  drawerOpenOptions.value = {
    ...options,
    drawerProps: {
      ...drawerOpenOptions.value.drawerProps, // 恢复function引用
      ...options.drawerProps,
    },
  };

  renderContent();
}

function setPreRowDisable(disabled = true) {
  isPreRowDisabled.value = disabled;
}

function setNextRowDisable(disabled = true) {
  isNextRowDisabled.value = disabled;
}

function onDrawerRefresh() {
  // 通过修改key实现dom重新创建
  domRefreshKey.value = Date.now();
}

/**
 * 新窗口打开
 * 仅支持：iframe模式
 * */
function onOpenTab() {
  if (!isOpenNewTabVisible.value) return;
  window.open(iframeSrc.value, '_blank');
}

/**
 * 新窗口打开
 * 仅支持：iframe模式
 * */
function onDrawerClose() {
  visible.value = false;
}

function getVisible() {
  return visible.value;
}

onMounted(() => {
  const onDrawerReady = drawerOpenOptions.value.drawerProps?.onDrawerReady
  if (!onDrawerReady) return;
  onDrawerReady({
    api: drawerInstance?.exposed,
  })
})

defineExpose({ openDrawer, onClose, onUnfold, onFold, getVisible, setPreRowDisable, setNextRowDisable });
</script>
<style lang="scss">
.mate-micro-drawer {
  z-index: 1001 !important;
  &.tit-drawer {
    overflow: inherit;
  }
  .n-drawer__mask {
    z-index: 1002 !important;
  }
  .n-drawer__container {
    z-index: 1003 !important;
  }
}

.mate-micro-drawer--component {
  .n-drawer__body {
    padding: 0;
  }
}

.mate-micro-drawer .n-drawer__header {
  padding: 10px !important;
}

.mate-micro-drawer .n-drawer__body {
  padding: 0 !important;
}
</style>
<style lang="scss" scoped>
.custom-drawer-title {
  &_wrap {
    display: flex;
    flex-direction: row;
  }

  &_left {
    display: flex;
    flex-direction: row;
  }

  &_content {
    line-height: 22px;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
    font-size: 14px;
    font-weight: 500;
    text-align: center;
  }

  &_right {
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
  }
}
.icon-btn {
  border-radius: 4px;
  padding: 4px 6px 0;
}

.icon_disabled {
  color: rgb(206, 208, 214);
}

.icon-btn:hover {
  background: rgba(156, 159, 172, 0.15);
}
</style>
